/** Cloudflare worker that holds the Square access token. The token never reaches the browser. */
const workerBaseUrl = "https://cool-wildflower-106e.pickenspassion.workers.dev";

export const catalogUrl = `${workerBaseUrl}/catalog`;
export const checkoutUrl = `${workerBaseUrl}/checkout`;

export type CheckoutLineItem = {
  variationId: string;
  quantity: number;
};

export type CheckoutResult = {
  checkoutUrl: string;
  orderId: string | null;
};

/** Sends the cart to POST /checkout and returns the Square checkout link. Throws an Error with a customer-readable message on failure. */
export async function createCheckout(items: CheckoutLineItem[]): Promise<CheckoutResult> {
  let response: Response;
  try {
    response = await fetch(checkoutUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
  } catch {
    throw new Error("We couldn't reach checkout. Please check your connection and try again.");
  }

  const payload: unknown = await response.json().catch(() => null);
  const record = typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : {};

  if (!response.ok) {
    throw new Error(readWorkerError(record) ?? `Checkout failed (error ${response.status}). Please try again.`);
  }

  const url = typeof record.checkoutUrl === "string" ? record.checkoutUrl : "";
  if (!url) {
    throw new Error(readWorkerError(record) ?? "Checkout didn't return a payment link. Please try again.");
  }

  return { checkoutUrl: url, orderId: typeof record.orderId === "string" ? record.orderId : null };
}

function readWorkerError(record: Record<string, unknown>): string | null {
  for (const key of ["error", "message", "detail"]) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
    if (typeof value === "object" && value !== null) {
      const nested = (value as Record<string, unknown>).message ?? (value as Record<string, unknown>).detail;
      if (typeof nested === "string" && nested.trim()) {
        return nested;
      }
    }
  }
  if (Array.isArray(record.errors)) {
    const details = record.errors
      .map((entry) => (typeof entry === "object" && entry !== null ? (entry as Record<string, unknown>).detail ?? (entry as Record<string, unknown>).message : entry))
      .filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
    if (details.length > 0) {
      return details.join(" ");
    }
  }
  return null;
}
